import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import { storeTokens, stripTokens } from "@/authClient";
import { useI18n } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full border border-pine/15 bg-white px-4 py-3 text-sm font-light text-bark placeholder:text-sand/60 focus:border-copper focus:outline-none transition-colors duration-300";

export default function AuthModal({ open, onClose, onAuth }) {
  const { t } = useI18n();
  const a = t.auth;
  const [mode, setMode] = useState("in");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/${mode === "in" ? "login" : "register"}`,
        form,
        { withCredentials: true }
      );
      storeTokens(data);
      const user = stripTokens(data);
      onAuth(user);
      onClose();
      setForm({ name: "", email: "", password: "" });
      toast.success(a.welcomeToast, { description: user.name || user.email });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail, a.errorGeneric));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-pine/60 p-4 backdrop-blur-sm"
          onClick={onClose}
          data-testid="auth-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-md border border-copper/25 bg-paper p-8 shadow-2xl shadow-pine/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Logo className="h-10 w-10" />
                <h2 className="font-serif text-2xl text-pine">{a.modalTitle}</h2>
              </div>
              <button type="button" onClick={onClose} data-testid="auth-modal-close" aria-label="Close" className="text-sand transition-colors hover:text-copper">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4">
              {mode === "up" && (
                <input
                  data-testid="auth-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls}
                  placeholder={a.nameLabel}
                  autoComplete="name"
                />
              )}
              <input
                type="email"
                data-testid="auth-email-input"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls}
                placeholder={a.emailLabel}
                autoComplete="email"
              />
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="auth-password-input"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className={`${inputCls} pr-12`}
                  placeholder={a.passwordLabel}
                  autoComplete={mode === "in" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  data-testid="auth-password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sand transition-colors hover:text-copper"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {error && (
                <p data-testid="auth-error" className="border border-copper/40 bg-cream px-3 py-2 text-xs text-copper">
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="auth-submit-button"
                disabled={loading}
                className="bg-pine px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-copper disabled:opacity-60"
              >
                {loading ? "…" : mode === "in" ? a.submitIn : a.submitUp}
              </button>
            </form>

            <button
              type="button"
              data-testid="auth-toggle-mode"
              onClick={() => {
                setMode((m) => (m === "in" ? "up" : "in"));
                setError("");
              }}
              className="mt-4 w-full text-center text-xs font-light text-sand transition-colors hover:text-copper"
            >
              {mode === "in" ? a.switchToUp : a.switchToIn}
            </button>

            <div className="my-5 flex items-center gap-4">
              <span className="h-px flex-1 bg-pine/10" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand">ou</span>
              <span className="h-px flex-1 bg-pine/10" />
            </div>

            <button
              type="button"
              data-testid="auth-google-button"
              onClick={googleLogin}
              className="flex w-full items-center justify-center gap-3 border border-pine/20 bg-white px-5 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-pine transition-all duration-300 hover:border-copper hover:text-copper"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
              </svg>
              {a.orGoogle}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
